/**
 * Case entity: backend DTOs, API calls, and the DTO -> display mapper.
 */

import type { CaseStep, HrCase } from '../../app/types';
import { apiGet, apiPost, apiPut } from '../apiClient';
import {
  caseKindToDisplay,
  caseRiskToDisplay,
  caseStatusToDisplay,
  formatDate,
} from './format';

/** Backend case-kind enum (camelCase on the wire). */
export type CaseKind =
  | 'grievance'
  | 'disciplinary'
  | 'sicknessAbsence'
  | 'leaverOffboarding'
  | 'flexibleWorking'
  | 'probation'
  | 'compliance'
  | 'conduct'
  | 'starterOnboarding'
  | 'other';

export type CaseStatusDto = 'open' | 'waiting' | 'escalated' | 'resolved';
export type CaseRiskDto = 'low' | 'medium' | 'high';
export type CaseStepStateDto = 'done' | 'current' | 'todo';

export interface CaseStepDto {
  title: string;
  detail?: string | null;
  state: CaseStepStateDto;
  completedAt?: string | null;
}

export interface CaseDto {
  id: string;
  companyId: string;
  title: string;
  kind: CaseKind;
  employeeId: string | null;
  personLabel: string | null;
  status: CaseStatusDto;
  risk: CaseRiskDto;
  summary: string | null;
  nextStep: string | null;
  steps: CaseStepDto[];
  relatedDocumentIds: string[];
  /** Captured per-case predicate answers (slug -> yes/no); empty until the questions are asked. */
  predicates: Record<string, boolean>;
  openedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  lastUpdated: string;
}

export interface CreateCaseRequest {
  companyId: string;
  title: string;
  kind: CaseKind;
  employeeId?: string;
  personLabel?: string;
  risk?: CaseRiskDto;
  summary?: string;
  nextStep?: string;
}

export interface UpdateCaseRequest {
  status?: CaseStatusDto;
  risk?: CaseRiskDto;
  summary?: string;
  nextStep?: string;
  /** Replace-all: the full ordered step list. */
  steps?: CaseStepDto[];
  /** Replaces the captured per-case answers. Omit to leave them untouched. */
  predicates?: Record<string, boolean>;
}

/** GET /api/cases?companyId={id}[&employeeId={id}]. */
export function listCases(companyId: string, employeeId?: string): Promise<CaseDto[]> {
  const query = new URLSearchParams({ companyId });
  if (employeeId) query.set('employeeId', employeeId);
  return apiGet<CaseDto[]>(`/api/cases?${query.toString()}`);
}

/** GET /api/cases/{id}. */
export function getCase(id: string): Promise<CaseDto> {
  return apiGet<CaseDto>(`/api/cases/${id}`);
}

/** POST /api/cases. */
export function createCase(request: CreateCaseRequest): Promise<CaseDto> {
  return apiPost<CaseDto>('/api/cases', request);
}

/** PUT /api/cases/{id} — status/risk/summary/nextStep/steps (steps replace-all). */
export function updateCase(id: string, request: UpdateCaseRequest): Promise<CaseDto> {
  return apiPut<CaseDto>(`/api/cases/${id}`, request);
}

function mapCaseStep(dto: CaseStepDto): CaseStep {
  return {
    title: dto.title,
    detail: dto.detail ?? undefined,
    state: dto.state,
  };
}

/** Build the display "opened" line, e.g. "Opened 13 Jul 2026 · Resolved 2 Aug 2026". */
function buildOpenedLine(dto: CaseDto): string {
  const opened = `Opened ${formatDate(dto.openedAt)}`;
  if (dto.resolvedAt) {
    return `${opened} · Resolved ${formatDate(dto.resolvedAt)}`;
  }
  return opened;
}

/** CaseDto -> display HrCase. */
export function mapCase(dto: CaseDto): HrCase {
  return {
    id: dto.id,
    title: dto.title,
    kind: caseKindToDisplay(dto.kind),
    person: dto.personLabel ?? undefined,
    employeeId: dto.employeeId ?? undefined,
    opened: buildOpenedLine(dto),
    status: caseStatusToDisplay(dto.status),
    risk: caseRiskToDisplay(dto.risk),
    summary: dto.summary ?? '',
    nextStep: dto.nextStep ?? undefined,
    steps: (dto.steps ?? []).map(mapCaseStep),
    // Arrives with the experts phase; no expert linkage on the current surface.
    expertId: undefined,
    relatedDocIds: dto.relatedDocumentIds ?? [],
  };
}
